// src/hooks/useAdminNav.ts
import { useState, useEffect, useCallback } from 'react';
import { useProjectContext } from '../contexts/ProjectContext';
import { PinManager, ProjectManager } from '../models';
import { IProject } from '../types';
import pinStore from '../stores/PinStore';
import userStore from '../stores/UserStore';
import { projectStore } from '../stores/ProjectStore';
import {
  canAccessPlatformAppTools,
} from '../utilities/platformAdminAccess';
import {
  isModeratorUserRole,
  isOrganisationAdminUserRole,
  isProjectAdminUserRole,
} from '../utilities/orgAccess';
import { isDemoMode } from '../utilities/demoMode';
import { DemoDataManager } from '../services/demoDataManager';

// Add return type interface
interface UseAdminNavReturn {
  projectName: string;
  projects: IProject[];
  onProjectChange: (projectKey: string) => void;
  selectedProject: string;
  userRole: string;
  selectedProjectDetails: IProject | undefined;
}

export const useAdminNav = (): UseAdminNavReturn => {
  const { selectedProject, setSelectedProject } = useProjectContext();
  const [projectName, setProjectName] = useState<string>('');
  const [projects, setProjects] = useState<IProject[]>([]);
  const userRole = userStore.user?.userRole || '';
  const [selectedProjectDetails, setSelectedProjectDetails] = useState<IProject>();

  // Fetch project name based on selectedProject
  useEffect(() => {
    let isMounted = true;
    const fetchProjectDetails = async () => {
      if (selectedProject) {
        // Check if in demo mode and organisational tutorial is active
        const isOrganisationalTutorial = isDemoMode() && localStorage.getItem('demo_tutorial_flow') === 'organisational-projects-tutorial';
        
        if (isOrganisationalTutorial) {
          // In organisational tutorial, check if it's a demo project
          // Ensure demo data is initialized
          let demoProjects = DemoDataManager.getDemoOrganisationProjects();
          if (demoProjects.length === 0) {
            DemoDataManager.initializeOrganisationalDemoData();
            demoProjects = DemoDataManager.getDemoOrganisationProjects();
          }
          
          const isDemoProject = demoProjects.some(p => p.project_id === selectedProject);
          console.log('Organisational tutorial - checking demo project:', { selectedProject, isDemoProject, demoProjectIds: demoProjects.map(p => p.project_id) });
          
          if (isDemoProject) {
            // It's a demo project, get it from demo data
            const demoProject = demoProjects.find(p => p.project_id === selectedProject);
            if (demoProject) {
              console.log('Loading demo project from demo data:', demoProject.projectName);
              setProjectName(demoProject.projectName || '');
              setSelectedProjectDetails(demoProject);
              return; // Exit early, don't check access
            }
          }
          // If not a demo project but in tutorial mode, still allow it (might be a project created during tutorial)
          console.log('In tutorial mode but not a demo project, allowing anyway');
          const project = await ProjectManager.getProject(selectedProject);
          if (project) {
            setProjectName(project.projectName || '');
            setSelectedProjectDetails(project);
          }
          return; // Exit early, don't check access in tutorial mode
        }

        // Check if user has access to this project (for demo users and projectAdmin)
        // Only check if NOT in organisational tutorial mode
        const isDemoUser = userStore.user?.userId === 'demo-user';
        const projectAdminAccess = userStore.user?.projectAdminAccess;
        
        if (isDemoUser || (isProjectAdminUserRole(userRole) && projectAdminAccess && projectAdminAccess.length > 0)) {
          if (!projectAdminAccess?.includes(selectedProject)) {
            console.warn('User does not have access to project:', selectedProject);
            setProjectName('');
            setSelectedProjectDetails(undefined);
            return;
          }
        }

        const project = await ProjectManager.getProject(selectedProject);
        if (project && isMounted) {
          setProjectName(project.projectName || '');
          setSelectedProjectDetails(project);
        }
      }
    };

    fetchProjectDetails();
    return () => {
      isMounted = false;
    };
  }, [selectedProject, userRole, userStore.user?.userId, userStore.user?.projectAdminAccess]);

  // Keep selectedProjectDetails in sync with live Firestore updates
  useEffect(() => {
    if (!selectedProject) return;

    let isMounted = true;
    let unsubscribe: (() => void) | undefined;

    const setupObserver = async () => {
      // Mirror the organisational tutorial demo behaviour: in that mode we rely on DemoDataManager,
      // so we skip attaching a Firestore listener here.
      const isOrganisationalTutorial =
        isDemoMode() && localStorage.getItem('demo_tutorial_flow') === 'organisational-projects-tutorial';

      if (isOrganisationalTutorial) {
        return;
      }

      unsubscribe = ProjectManager.observeProject(selectedProject, (project) => {
        if (!isMounted) return;

        if (!project) {
          setProjectName('');
          setSelectedProjectDetails(undefined);
          return;
        }

        setProjectName(project.projectName || '');
        setSelectedProjectDetails(project);
      });
    };

    setupObserver();

    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [selectedProject]);

  useEffect(() => {
    const fetchProjects = async () => {
      console.info('[OVH:FacilitatorDash] AdminNav:fetchProjects:start', {
        userRole,
        selectedProject,
        orgId: userStore.currentOrganisation?.id,
      });
      try {
        // Check if in demo mode and organisational tutorial is active
        if (isDemoMode() && localStorage.getItem('demo_tutorial_flow') === 'organisational-projects-tutorial') {
          // Ensure demo organisation is set
          let demoOrg = DemoDataManager.getDemoOrganisation();
          if (!demoOrg) {
            demoOrg = DemoDataManager.initializeOrganisationalDemoData();
          }
          if (demoOrg && (!userStore.currentOrganisation || userStore.currentOrganisation.id !== demoOrg.id)) {
            userStore.setCurrentOrganisation(demoOrg);
          }
          // Load demo projects
          const demoProjects = DemoDataManager.getDemoOrganisationProjects();
          setProjects(demoProjects);
          
          // Select first project if none selected
          if (demoProjects.length > 0 && (!selectedProject || !demoProjects.find(p => p.project_id === selectedProject))) {
            const firstProject = demoProjects[0];
            setSelectedProject(firstProject.project_id);
            setProjectName(firstProject.projectName || '');
            setSelectedProjectDetails(firstProject);
          }
          return;
        }

        let projectsList;
        if (isModeratorUserRole(userRole)) {
          projectsList = await ProjectManager.observeModeratorProjects(userStore.currentUser?.id || '');
        } else if (isOrganisationAdminUserRole(userRole) && userStore.currentOrganisation) {
          projectsList = await ProjectManager.observeOrganisationProjects(userStore.currentOrganisation.id);
        } else {
          projectsList = await ProjectManager.observeProjects();
        }

        // Filter projects based on user access for demo users and projectAdmin users
        const isDemoUser = userStore.user?.userId === 'demo-user';
        const projectAdminAccess = userStore.user?.projectAdminAccess;
        
        if (isDemoUser || (isProjectAdminUserRole(userRole) && projectAdminAccess && projectAdminAccess.length > 0)) {
          // Only show projects the user has access to
          projectsList = projectsList.filter(project => 
            projectAdminAccess?.includes(project.project_id)
          );
        }

        setProjects(projectsList);

        // If no project is selected or the selected project isn't in the filtered list, select the first available project
        if (projectsList.length > 0 && (!selectedProject || !projectsList.find(p => p.project_id === selectedProject))) {
          const firstProject = projectsList[0];
          console.info('[OVH:FacilitatorDash] AdminNav:auto-select-project', {
            from: selectedProject,
            to: firstProject.project_id,
          });
          setSelectedProject(firstProject.project_id);
          setProjectName(firstProject.projectName || '');
          setSelectedProjectDetails(firstProject);
        } else if (selectedProject && !projectsList.find(p => p.project_id === selectedProject)) {
          // If selected project is not in accessible list, clear it
          setSelectedProject('');
          setProjectName('');
          setSelectedProjectDetails(undefined);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    // Platform admins (superAdmin + limited admin) fetch all projects for Pins / Moderation
    if (canAccessPlatformAppTools(userRole)) {
      fetchProjects();
    } else if (userStore.currentOrganisation || isProjectAdminUserRole(userRole) || userStore.user?.userId === 'demo-user') {
      fetchProjects();
    }
  }, [userRole, userStore.currentUser?.id, userStore.currentOrganisation, userStore.user?.userId, userStore.user?.projectAdminAccess, selectedProject, setSelectedProject]);

  // Handle project change
  const onProjectChange = useCallback((projectKey: string) => {
    // Verify the project exists in our list before changing
    const projectExists = projects.find(p => p.project_id === projectKey);
    if (!projectExists) {
      console.error('Attempted to select a project that does not exist in the available projects list');
      return;
    }

    // Avoid clearing pins when re-selecting the same project (e.g. loadSavedProject on mount)
    if (projectKey === selectedProject) {
      return;
    }

    pinStore.clearPins();
  
    const fetchPinsForProject = async () => {
      try {
        const pins = await PinManager.getPinsByProjectKey(projectKey, true); // Include demo for admin console
        pinStore.setPins(pins);
      } catch (error) {
        console.error('Error fetching pins for project:', error);
      }
    };
  
    if (projectKey) {
      fetchPinsForProject();
      setSelectedProject(projectKey);
      projectStore.setSelectedProject(projectKey);
      localStorage.setItem('selectedProject', projectKey);
    }
  }, [setSelectedProject, projects, selectedProject]);

  // Load saved project on mount
  useEffect(() => {
    const loadSavedProject = async () => {
      // Check if in demo mode and organisational tutorial is active
      const isOrganisationalTutorial = isDemoMode() && localStorage.getItem('demo_tutorial_flow') === 'organisational-projects-tutorial';
      
      if (isOrganisationalTutorial) {
        // In organisational tutorial, only allow demo projects
        const demoProjects = DemoDataManager.getDemoOrganisationProjects();
        const savedProject = localStorage.getItem('selectedProject');
        
        // Check if saved project is a demo project
        if (savedProject && demoProjects.some(p => p.project_id === savedProject)) {
          // It's a valid demo project, select it if it's in the projects list
          if (projects.length > 0 && projects.some(p => p.project_id === savedProject)) {
            onProjectChange(savedProject);
          } else if (projects.length === 0) {
            // Projects not loaded yet, wait for them to load
            // The fetchProjects effect will handle selecting it
          }
        } else {
          // Clear invalid saved project and select first demo project
          if (savedProject) {
            localStorage.removeItem('selectedProject');
            console.warn('Saved project not accessible, clearing from localStorage');
          }
          // Select first demo project if projects are loaded
          if (demoProjects.length > 0 && projects.length > 0) {
            const firstDemoProject = demoProjects[0];
            setSelectedProject(firstDemoProject.project_id);
            setProjectName(firstDemoProject.projectName || '');
            setSelectedProjectDetails(firstDemoProject);
          }
        }
        return;
      }

    const savedProject = localStorage.getItem('selectedProject');
    if (savedProject && projects.length > 0) {
      const projectExists = projects.find(p => p.project_id === savedProject);
      
      // Additional validation for demo users and projectAdmin users
      const isDemoUser = userStore.user?.userId === 'demo-user';
      const projectAdminAccess = userStore.user?.projectAdminAccess;
      
      if (projectExists) {
        // For demo users and projectAdmin, verify access before restoring
        if (isDemoUser || (isProjectAdminUserRole(userRole) && projectAdminAccess && projectAdminAccess.length > 0)) {
            // In organisational tutorial mode, allow demo projects
            if (isOrganisationalTutorial) {
              const demoProjects = DemoDataManager.getDemoOrganisationProjects();
              const isDemoProject = demoProjects.some(p => p.project_id === savedProject);
              if (isDemoProject || projectAdminAccess?.includes(savedProject)) {
                onProjectChange(savedProject);
              } else {
                // Clear invalid saved project from localStorage
                localStorage.removeItem('selectedProject');
                console.warn('Saved project not accessible, clearing from localStorage');
                // Select first demo project
                if (demoProjects.length > 0) {
                  const firstDemoProject = demoProjects[0];
                  setSelectedProject(firstDemoProject.project_id);
                  setProjectName(firstDemoProject.projectName || '');
                  setSelectedProjectDetails(firstDemoProject);
                }
              }
            } else if (projectAdminAccess?.includes(savedProject)) {
            onProjectChange(savedProject);
          } else {
            // Clear invalid saved project from localStorage
            localStorage.removeItem('selectedProject');
            console.warn('Saved project not accessible, clearing from localStorage');
          }
        } else {
          onProjectChange(savedProject);
        }
      }
    }
    };

    loadSavedProject();
  }, [projects, onProjectChange, userRole, userStore.user?.userId, userStore.user?.projectAdminAccess, setSelectedProject, setProjectName, setSelectedProjectDetails]);

  return {
    projectName,
    projects,
    onProjectChange,
    selectedProject,
    userRole,
    selectedProjectDetails,
  };
};
